import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FullCalendarModule } from '@fullcalendar/angular';
import multiMonthPlugin from '@fullcalendar/multimonth'
import interactionPlugin from '@fullcalendar/interaction';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FuseLoadingService } from '@fuse/services/loading';
import { CalendarView } from 'angular-calendar';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { SubRouteFrequenciesService } from '../../sub-routes-frequency/sub-route-frequency.service';
import { ICreateAppointment } from '../../sub-routes-frequency/interfaces/create-sub-route-bp-frequency';

@Component({
    selector: 'app-custom-dates',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        FullCalendarModule,
        MatIconModule,
        MatDatepickerModule,
        MatFormFieldModule,
        FormsModule,
        MatInputModule,
        MatTooltipModule
    ],
    templateUrl: './modal-w-frequencies.component.html',
    styleUrls: ['./modal-w-frequencies.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CustomDatesModalComponent {

    view: CalendarView = CalendarView.Month;
    calendarOptions: any = {
        plugins: [multiMonthPlugin, interactionPlugin],
        initialView: 'multiMonthFourMonth',
        views: {
            multiMonthFourMonth: {
                type: 'multiMonth',
                duration: { months: 12 },
                buttonText: 'Year',
            },
        },
        events: [],
        headerToolbar: false,
        locale: 'es'
    };

    customDates: { id: string, date: Date, businessPartners?: any[] }[] = [];
    newDate: Date | null = null;
    selectedDateForEdit: { id: string, date: Date, _id: string, businessPartners?: any } | null = null;


    constructor(
        public dialogRef: MatDialogRef<CustomDatesModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private snackBar: MatSnackBar,
        private _fuseLoadingService: FuseLoadingService,
        private frequencyService: SubRouteFrequenciesService,
    ) {

    }

    ngOnInit(): void {
        this.getAppointment();
        this.dialogRef.updateSize("90%");
    }

    getAppointment() {
        const now = new Date();
        const nowUTC = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
        this.frequencyService.findAppointmentWithCountByBusiness(this.data.frequencies.id, this.data.subRoute.id, nowUTC).subscribe({
            next: (res) => {
                if (res.status === 'success' && res.data && res.data.length > 0) {
                    // Extract the array of frequency data from the response
                    // res.data[0].data = res.data[0].data || [];
                    const frequencyData = res.data;
                    this.loadCustomDates(frequencyData);
                }
            },
            error: (err) => {
                this.snackBar.open(err.error.message || "Error al obtener las frecuencias", "Cerrar", { duration: 5000 });
            },
        });
    }

    loadCustomDates(frequencyData: any[]): void {
        this._fuseLoadingService.show();

        // Filtramos solo las frecuencias personalizadas (type: 'C')
        const customFrequencies = frequencyData.filter((f: any) => f.type === 'C');

        // Mapeamos las fechas para la lista y el calendario
        this.customDates = customFrequencies.map((frequency: any, index: number) => ({
            id: `${index}-${frequency._id}`,  // Using the database _id for uniqueness
            _id: frequency._id,
            date: new Date(frequency.date)
        }));

        this.updateCalendarEvents();
        this._fuseLoadingService.hide();
    }

    updateCalendarEvents(): void {
        this.calendarOptions.events = this.customDates.map(dateObj => ({
            id: dateObj.id,
            title: '',
            start: dateObj.date,
            allDay: true,
        }));
    }

    // addDate(): void {
    //     if (this.newDate) {
    //         const newDateObj = {
    //             id: `new-${Date.now()}`,
    //             date: new Date(this.newDate)
    //         };

    //         if (!this.dateExists(newDateObj.date)) {
    //             this.customDates.push(newDateObj);
    //             this.updateCalendarEvents();
    //             this.newDate = null;
    //         } else {
    //             this.snackBar.open('Esta fecha ya existe', 'Cerrar', { duration: 3000 });
    //         }
    //     }
    // }

    async addDate(): Promise<void> {
        if (!this.newDate) return;

        const newDate = new Date(this.newDate);

        if (this.dateExists(newDate)) {
            this.snackBar.open('Esta fecha ya existe', 'Cerrar', { duration: 3000 });
            return;
        }

        try {
            const bpIds = [this.data.frequencies._id.toString()];
            const createFrequency: any = {
                type: 'C',
                subRouteId: this.data.subRoute._id.toString(),
                dates: [newDate.toISOString()],
                businessPartners: (bpIds || []).map((b, index) => ({
                    businessPartnerId: b,
                })),
            };

            // 2. Create the new frequency in backend
            const result = await this.frequencyService.createManyAppointment(createFrequency).toPromise();

            // 3. If successful, update local state
            const newDateObj = {
                id: `new-${Date.now()}`,
                date: new Date(this.newDate)
            };

            this.customDates.push(newDateObj);
            this.updateCalendarEvents();
            this.newDate = null;

            this.snackBar.open('Fecha agregada correctamente', 'Cerrar', {
                duration: 5000,
                verticalPosition: 'top'
            });

        } catch (error) {
            this.snackBar.open('Error al agregar fecha', 'Cerrar', {
                duration: 5000,
                verticalPosition: 'top'
            });
        }
    }

    dateExists(date: Date): boolean {
        return this.customDates.some(d =>
            d.date.getTime() === date.getTime()
        );
    }

    selectDateForEdit(dateObj: { id: string, date: Date, _id: string }): void {
        this.selectedDateForEdit = { ...dateObj };
    }

    // updateDate(): void {
    //     if (this.selectedDateForEdit) {
    //         const index = this.customDates.findIndex(d => d.id === this.selectedDateForEdit.id);
    //         if (index !== -1) {
    //             this.customDates[index] = {
    //                 id: this.selectedDateForEdit.id,
    //                 date: new Date(this.selectedDateForEdit.date)  
    //             };
    //             this.updateCalendarEvents();
    //             this.selectedDateForEdit = null;
    //             this.snackBar.open('Fecha actualizada', 'Cerrar', { duration: 3000 });
    //         }
    //     }
    // }

    // removeDate(dateObj: { id: string, date: Date }): void {
    //     this.customDates = this.customDates.filter(d => d.id !== dateObj.id);
    //     this.updateCalendarEvents();
    //     if (this.selectedDateForEdit?.id === dateObj.id) {
    //         this.selectedDateForEdit = null;
    //     }
    // }

    removeDate(dateObj: { id: string, date: Date, _id: string }): void {
        const frequencyId = dateObj._id;

        const bpIds = [this.data.frequencies._id.toString()];
        this._fuseLoadingService.show();

        this.frequencyService.deleteBusinessPartnerAppointment(frequencyId, bpIds)
            .subscribe({
                next: () => {
                    this.customDates = this.customDates.filter(d => d.id !== dateObj.id);
                    this.updateCalendarEvents();

                    if (this.selectedDateForEdit?.id === dateObj.id) {
                        this.selectedDateForEdit = null;
                    }

                    this.snackBar.open("Fecha y registros asociados eliminados con éxito", "Cerrar", {
                        duration: 5000,
                        verticalPosition: "top"
                    });

                    this._fuseLoadingService.hide();
                },
                error: (err) => {
                    this._fuseLoadingService.hide();
                    this.snackBar.open(
                        err.error.message || "Error al eliminar la fecha y registros asociados",
                        "Cerrar",
                        { duration: 5000, verticalPosition: "top" }
                    );
                },
            });
    }

    async updateDate(): Promise<void> {
        if (!this.selectedDateForEdit) return;
        this._fuseLoadingService.show();
        try {
            const bpIds: any = [this.data.frequencies._id.toString()];

            // 1. Eliminar la fecha antigua con sus business partners
            const change = await this.frequencyService.deleteBusinessPartnerAppointment(
                this.selectedDateForEdit._id,
                bpIds
            ).toPromise();

            // 2. Preparar los datos para la nueva fecha
            const createFrequency = {
                type: 'C',
                subRouteId: this.data.subRoute._id.toString(),
                dates: [this.selectedDateForEdit.date],
                businessPartners: (bpIds || []).map((b, index) => ({
                    businessPartnerId: b,
                })),
            };


            // 3. Crear la nueva fecha
            await this.frequencyService.createManyAppointment(createFrequency).toPromise();

            // 4. Actualizar la vista 
            const index = this.customDates.findIndex(d => d.id === this.selectedDateForEdit.id);
            if (index !== -1) {
                this.customDates[index] = {
                    id: this.selectedDateForEdit.id,
                    date: new Date(this.selectedDateForEdit.date),
                    businessPartners: this.selectedDateForEdit.businessPartners
                };
            }

            this.updateCalendarEvents();
            this.selectedDateForEdit = null;

            this.snackBar.open('Fecha actualizada correctamente', 'Cerrar', {
                duration: 5000,
                verticalPosition: 'top'
            });

        } catch (err) {
            this.snackBar.open(
                err.error?.message || 'Error al actualizar la fecha',
                'Cerrar',
                { duration: 5000, verticalPosition: 'top' }
            );
        } finally {
            this._fuseLoadingService.hide();
        }
    }


    handleEventDrop(info: any): void {
        const event = info.event;
        const dateObjIndex = this.customDates.findIndex(d => d.id === event.id);

        if (dateObjIndex !== -1) {
            this.customDates[dateObjIndex].date = event.start;
            this.snackBar.open('Fecha actualizada', 'Cerrar', { duration: 3000 });
        }
    }

    // handleEventClick(info: any): void {
    //     if (confirm('¿Desea eliminar esta fecha programada?')) {
    //         this.removeDate({ id: info.event.id, date: info.event.start });
    //     }
    // }

    onSave(): void {
        const frequenciesToSave = [{
            type: 'C',
            dates: this.customDates.map(d => d.date.toISOString()),
            days: []
        }];

        this.dialogRef.close(frequenciesToSave);
    }


}